import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "driver", label: "Driver" },
  { value: "wood", label: "Wood" },
  { value: "jern", label: "Jern" },
  { value: "putter", label: "Putter" },
  { value: "bag", label: "Bag" },
  { value: "wedge", label: "Wedge" },
  { value: "hybrid", label: "Hybrid" },
  { value: "tilbehør", label: "Tilbehør" },
  { value: "annet", label: "Annet" },
];

const conditions = [
  { value: "ny", label: "Ny" },
  { value: "utmerket", label: "Utmerket" },
  { value: "god", label: "God" },
  { value: "greit", label: "Greit" },
];

const flexOptions = [
  { value: "ladies", label: "Ladies (L)" },
  { value: "senior", label: "Senior (A)" },
  { value: "regular", label: "Regular (R)" },
  { value: "stiff", label: "Stiff (S)" },
  { value: "x-stiff", label: "Extra Stiff (X)" },
];

const lengthOptions = [
  { value: "oversized", label: "Oversized (+)" },
  { value: "standard", label: "Standard" },
  { value: "undersized", label: "Undersized (-)" },
];

const handOptions = [
  { value: "right", label: "Høyre" },
  { value: "left", label: "Venstre" },
];

const shaftMaterialOptions = [
  { value: "graphite", label: "Graphite" },
  { value: "steel", label: "Steel" },
];

const lieOptions = [
  { value: "-2", label: "2° Flat" },
  { value: "-1.5", label: "1.5° Flat" },
  { value: "-1", label: "1° Flat" },
  { value: "-0.5", label: "0.5° Flat" },
  { value: "0", label: "0° (Standard)" },
  { value: "0.5", label: "0.5° Upright" },
  { value: "1", label: "1° Upright" },
  { value: "1.5", label: "1.5° Upright" },
  { value: "2", label: "2° Upright" },
];

const brandOptions = [
  { value: "callaway", label: "Callaway" },
  { value: "taylormade", label: "TaylorMade" },
  { value: "titleist", label: "Titleist" },
  { value: "ping", label: "Ping" },
  { value: "cobra", label: "Cobra" },
  { value: "mizuno", label: "Mizuno" },
  { value: "cleveland", label: "Cleveland" },
  { value: "srixon", label: "Srixon" },
  { value: "wilson", label: "Wilson" },
  { value: "odyssey", label: "Odyssey" },
  { value: "scotty cameron", label: "Scotty Cameron" },
  { value: "annet", label: "Annet" },
];

const EditListingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    condition: "",
    location: "",
    images: "",
    seller_name: "",
    seller_email: "",
    seller_phone: "",
    flex: "",
    length: "",
    hand: "",
    brand: "",
    shaft_material: "",
    lie: "",
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error("Du må logge inn for å redigere en annonse");
      navigate("/login", { state: { from: `/edit/${id}` } });
      return;
    }
    fetchListing();
  }, [id, isAuthenticated, navigate]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      const listing = response.data;
      
      // Check if user is the creator
      const isCreator = listing.creator_id === user?.id;
      const isOldListing = !listing.creator_id;
      
      if (!isCreator && !isOldListing) {
        toast.error("Du har ikke tillatelse til å redigere denne annonsen");
        navigate(`/listing/${id}`);
        return;
      }
      
      if (isOldListing) {
        toast.error("Denne annonsen kan kun redigeres av admin");
        navigate(`/listing/${id}`);
        return;
      }
      
      setCanEdit(true);
      setFormData({
        title: listing.title,
        description: listing.description,
        price: listing.price.toString(),
        category: listing.category,
        condition: listing.condition,
        location: listing.location,
        images: listing.images.join(", "),
        seller_name: listing.seller_name,
        seller_email: listing.seller_email,
        seller_phone: listing.seller_phone,
        flex: listing.flex || "",
        length: listing.length || "",
        hand: listing.hand || "",
        brand: listing.brand || "",
        shaft_material: listing.shaft_material || "",
        lie: listing.lie || "",
      });
      // Set existing images as uploaded images for preview
      setUploadedImages(listing.images || []);
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Kunne ikke laste inn annonse");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();
        if (data.secure_url) {
          uploadedUrls.push(data.secure_url);
        }
      }

      setUploadedImages([...uploadedImages, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} bilde(r) lastet opp!`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Kunne ikke laste opp bilder");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setUploadedImages(uploadedImages.filter((_, index) => index !== indexToRemove));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.price || !formData.category || 
        !formData.condition || !formData.location || !formData.seller_name || 
        !formData.seller_email || !formData.seller_phone) {
      toast.error("Vennligst fyll ut alle felt");
      return;
    }

    try {
      setSaving(true);
      
      // Combine uploaded images with any manually entered URLs
      const manualUrls = formData.images
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url.length > 0);
      
      const allImages = [...uploadedImages, ...manualUrls];

      const listingData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        condition: formData.condition,
        location: formData.location,
        images: allImages,
        seller_name: formData.seller_name,
        seller_email: formData.seller_email,
        seller_phone: formData.seller_phone,
        // Only include optional fields if they have values
        ...(formData.flex && { flex: formData.flex }),
        ...(formData.length && { length: formData.length }),
        ...(formData.hand && { hand: formData.hand }),
        ...(formData.brand && { brand: formData.brand }),
        ...(formData.shaft_material && { shaft_material: formData.shaft_material }),
        ...(formData.lie && { lie: formData.lie }),
      };

      await axios.put(`${API}/listings/${id}`, listingData, {
        headers: getAuthHeaders(),
      });
      toast.success("Annonse oppdatert!");
      navigate(`/listing/${id}`);
    } catch (error) {
      console.error("Error updating listing:", error);
      if (error.response?.status === 401) {
        toast.error("Du må logge inn for å redigere annonsen");
        navigate("/login", { state: { from: `/edit/${id}` } });
      } else if (error.response?.status === 403) {
        toast.error("Du har ikke tillatelse til å redigere denne annonsen");
      } else {
        toast.error("Kunne ikke oppdatere annonse");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !canEdit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center">
        <p className="text-gray-500">Laster...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 pb-24">
      {/* Navigation */}
      <Navbar showBackButton={true} backTo={`/listing/${id}`} showWedgeGuide={false} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-gray-900">
              Rediger annonse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title">Tittel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="F.eks. Callaway Driver 2023"
                  className="mt-1"
                  data-testid="title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Beskrivelse *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Beskriv utstyret ditt i detalj..."
                  rows={4}
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              {/* Price and Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Pris (kr) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    placeholder="2500"
                    className="mt-1"
                    data-testid="price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Lokasjon *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    placeholder="Oslo"
                    className="mt-1"
                    data-testid="location-input"
                  />
                </div>
              </div>

              {/* Category and Condition */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Kategori *</Label>
                  <Select value={formData.category} onValueChange={(value) => handleChange("category", value)}>
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Velg kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tilstand *</Label>
                  <Select value={formData.condition} onValueChange={(value) => handleChange("condition", value)}>
                    <SelectTrigger className="mt-1" data-testid="condition-select">
                      <SelectValue placeholder="Velg tilstand" />
                    </SelectTrigger>
                    <SelectContent>
                      {conditions.map((cond) => (
                        <SelectItem key={cond.value} value={cond.value}>
                          {cond.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Advanced Specifications */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Spesifikasjoner (valgfritt)</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Merke</Label>
                    <Select value={formData.brand} onValueChange={(value) => handleChange("brand", value)}>
                      <SelectTrigger className="mt-1" data-testid="brand-select">
                        <SelectValue placeholder="Velg merke" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandOptions.map((brand) => (
                          <SelectItem key={brand.value} value={brand.value}>
                            {brand.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Hånd (høyre/venstre)</Label>
                    <Select value={formData.hand} onValueChange={(value) => handleChange("hand", value)}>
                      <SelectTrigger className="mt-1" data-testid="hand-select">
                        <SelectValue placeholder="Velg hånd" />
                      </SelectTrigger>
                      <SelectContent>
                        {handOptions.map((hand) => (
                          <SelectItem key={hand.value} value={hand.value}>
                            {hand.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label>Stivhet/Flex</Label>
                    <Select value={formData.flex} onValueChange={(value) => handleChange("flex", value)}>
                      <SelectTrigger className="mt-1" data-testid="flex-select">
                        <SelectValue placeholder="Velg flex" />
                      </SelectTrigger>
                      <SelectContent>
                        {flexOptions.map((flex) => (
                          <SelectItem key={flex.value} value={flex.value}>
                            {flex.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Lengde</Label>
                    <Select value={formData.length} onValueChange={(value) => handleChange("length", value)}>
                      <SelectTrigger className="mt-1" data-testid="length-select">
                        <SelectValue placeholder="Velg lengde" />
                      </SelectTrigger>
                      <SelectContent>
                        {lengthOptions.map((length) => (
                          <SelectItem key={length.value} value={length.value}>
                            {length.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Shaft-materiale</Label>
                    <Select value={formData.shaft_material} onValueChange={(value) => handleChange("shaft_material", value)}>
                      <SelectTrigger className="mt-1" data-testid="shaft-material-select">
                        <SelectValue placeholder="Velg materiale" />
                      </SelectTrigger>
                      <SelectContent>
                        {shaftMaterialOptions.map((material) => (
                          <SelectItem key={material.value} value={material.value}>
                            {material.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Lie vinkel (for jern)</Label>
                  <Select value={formData.lie} onValueChange={(value) => handleChange("lie", value)}>
                    <SelectTrigger className="mt-1" data-testid="lie-select">
                      <SelectValue placeholder="Velg lie vinkel" />
                    </SelectTrigger>
                    <SelectContent>
                      {lieOptions.map((lie) => (
                        <SelectItem key={lie.value} value={lie.value}>
                          {lie.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500 mt-1">
                    Lie vinkelen påvirker hvordan køllen ligger mot bakken ved impact
                  </p>
                </div>
              </div>

              {/* Images */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Bilder</h3>
                
                {/* Image Guidelines */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Tips for gode bilder</h4>
                      <ul className="text-sm text-blue-800 space-y-1.5">
                        <li><strong>Antall:</strong> Last opp minst 3-5 bilder for best resultat</li>
                        <li><strong>Kølleansikt/riller:</strong> Ta et tydelig nærbildet som viser ansiktet og rillene på køllen</li>
                        <li><strong>Grep:</strong> Ta et tydelig bilde av grepet for å vise tilstand</li>
                        <li><strong>Helhet:</strong> Ta 2-3 bilder av hele køllen fra forskjellige vinkler</li>
                        <li><strong>Kvalitet:</strong> God belysning, skarpt fokus, og rent bakgrunn gir best inntrykk</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Image Upload */}
                <div className="mb-4">
                  <Label htmlFor="image-upload-edit" className="block mb-2">
                    Last opp nye bilder
                  </Label>
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="image-upload-edit"
                      className="cursor-pointer inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Velg bilder
                    </label>
                    <input
                      id="image-upload-edit"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImages}
                      data-testid="image-upload-input"
                    />
                    {uploadingImages && (
                      <span className="text-sm text-gray-600">Laster opp...</span>
                    )}
                  </div>
                </div>

                {/* Current Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mb-4">
                    <Label className="block mb-2">Nåværende bilder ({uploadedImages.length})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {uploadedImages.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Image ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-image-${index}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual URL Input (Optional) */}
                <div>
                  <Label htmlFor="images">Eller legg til bilde-URLer manuelt (valgfritt)</Label>
                  <Input
                    id="images"
                    value={formData.images}
                    onChange={(e) => handleChange("images", e.target.value)}
                    placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                    className="mt-1"
                    data-testid="images-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Legg til bilde-URLer separert med komma
                  </p>
                </div>
              </div>

              {/* Seller Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Kontaktinformasjon</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="seller_name">Navn *</Label>
                    <Input
                      id="seller_name"
                      value={formData.seller_name}
                      onChange={(e) => handleChange("seller_name", e.target.value)}
                      placeholder="Ditt navn"
                      className="mt-1"
                      data-testid="seller-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seller_email">E-post *</Label>
                    <Input
                      id="seller_email"
                      type="email"
                      value={formData.seller_email}
                      onChange={(e) => handleChange("seller_email", e.target.value)}
                      placeholder="din@email.no"
                      className="mt-1"
                      data-testid="seller-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seller_phone">Telefon *</Label>
                    <Input
                      id="seller_phone"
                      value={formData.seller_phone}
                      onChange={(e) => handleChange("seller_phone", e.target.value)}
                      placeholder="+47 123 45 678"
                      className="mt-1"
                      data-testid="seller-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4 flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/listing/${id}`)}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={saving}
                  data-testid="save-listing-button"
                >
                  {saving ? "Lagrer..." : "Lagre endringer"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditListingPage;
